/**
 * `defer-templates-marketplace` v0 — wire shapes for the curated
 * "showcase" tier of the Templates marketplace.
 *
 * Locked decisions (see `docs/handbook/features/pending/templates-marketplace.md`):
 *   - v0 is curated-Rynora-only: 5 templates, no community uploads,
 *     no payments, no ratings (locked decisions #1, #4).
 *   - Curated content is build-time-bundled JSON shipped from
 *     `@rynora/flow-templates-curated`; the API just reads from there.
 *   - The wire shape carries the same `UserFlowTemplatePayload` that
 *     user-saved templates use so the SPA hands the same payload to the
 *     same `instantiateTemplate` path.
 *
 * The marketplace is NOT stored in any DB table. The single mutation
 * (`POST /:templateId/insert`) creates a new `test_cases` row by
 * delegating to the existing flow-create path; the marketplace
 * itself stays read-only.
 */

import { z } from 'zod';
import { UserFlowTemplatePayloadSchema } from './flow-templates';

/**
 * The five v0 personas (locked decision #6). Adding a sixth requires
 * a tracker entry + a new JSON in `@rynora/flow-templates-curated`;
 * never enumerate ad-hoc.
 */
export const MarketplacePersonaSchema = z.enum([
  'login',
  'checkout',
  'search-and-filter',
  'signup-with-email-verification',
  'contact-form',
]);
export type MarketplacePersona = z.infer<typeof MarketplacePersonaSchema>;

/**
 * One curated marketplace template. Identical-shaped to
 * `UserFlowTemplatePayload` plus three card-display fields
 * (`persona`, `shortDescription`, `longDescription`). Thumbnail
 * generation deferred to v0.1 — v0 cards render a persona icon.
 */
export const MarketplaceTemplateSchema = z.object({
  /** Stable kebab-case slug; matches the curated JSON filename. */
  id: z.string().min(1).max(128),
  persona: MarketplacePersonaSchema,
  /** Card title; also becomes the new flow's name on insert. */
  name: z.string().min(1).max(160),
  /** One-liner shown on the marketplace card. */
  shortDescription: z.string().min(1).max(280),
  /** Multi-line copy shown in the preview pane / landing-page row. */
  longDescription: z.string().min(1).max(2048),
  /** The flow definition itself — re-uses the `UserFlowTemplatePayload` wire. */
  payload: UserFlowTemplatePayloadSchema,
});
export type MarketplaceTemplate = z.infer<typeof MarketplaceTemplateSchema>;

/** GET /api/marketplace/templates */
export const ListMarketplaceTemplatesResponseSchema = z.object({
  templates: z.array(MarketplaceTemplateSchema),
});
export type ListMarketplaceTemplatesResponse = z.infer<typeof ListMarketplaceTemplatesResponseSchema>;

/** POST /api/marketplace/templates/:templateId/insert */
export const InsertMarketplaceTemplateRequestSchema = z.object({
  /** The destination project the new flow lands under. */
  projectId: z.string().uuid(),
});
export type InsertMarketplaceTemplateRequest = z.infer<typeof InsertMarketplaceTemplateRequestSchema>;

export const InsertMarketplaceTemplateResponseSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  projectId: z.string().uuid(),
});
export type InsertMarketplaceTemplateResponse = z.infer<typeof InsertMarketplaceTemplateResponseSchema>;
